import { Box, Flex, Table, Thead, Tbody, Tr, Td, Th } from '@chakra-ui/react';
import {
  Getter,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import _ from 'lodash';
import React from 'react';

import { Icon as FaIcon } from 'packages/common-frontend/components/icons';
import { Select } from 'packages/common-frontend/components/Select';
import { TanStackPaginationControl } from 'packages/common-frontend/components/TanStack';
import { dollarNumberWithCommas } from 'packages/common-frontend/utils';
import { formatOwnerOptions } from 'packages/loanProduct/ui/utils';

// @jendevelops TODO move assignment receivable to finley-types
type AssignmentReceivable = any;

type ReceivableTableProps = {
  data: any[];
  // eslint-disable-next-line react/no-unused-prop-types
  columns: string[];
  creditFacilitiesMap: {
    [key: string]: { id: string; borrowerEntityName: string };
  };
};
export const ReceivableTable = ({
  data,
  creditFacilitiesMap,
}: ReceivableTableProps) => {
  const table = useReactTable({
    data,
    columns: [
      // Required Columns
      {
        id: 'externalReceivableId',
        accessorKey: 'externalReceivableId',
        header: () => 'Receivable Id',
        /* eslint-disable react/display-name */
        cell: ({ getValue }) => getValue() || '-',
        enableSorting: false,
        enableColumnFilter: true,
      },
      {
        id: 'exposure',
        accessorKey: 'exposure',
        header: () => 'Exposure',
        /* eslint-disable react/display-name */
        cell: ({ getValue }) => {
          return dollarNumberWithCommas(getValue()) || '-';
        },
        enableSorting: true,
        enableColumnFilter: true,
      },
      {
        id: 'borrowing_base_amount',
        accessorKey: 'borrowingBaseAmount',
        header: () => 'Borrowing Base Amount',
        /* eslint-disable react/display-name */
        cell: ({ getValue }) => dollarNumberWithCommas(getValue()) || '-',
        enableSorting: true,
        enableColumnFilter: true,
      },
      {
        id: 'owner',
        accessorKey: 'owner',
        header: () => 'Proposed Owner',
        /* eslint-disable react/display-name */
        cell: ({
          row,
          getValue,
        }: {
          row: AssignmentReceivable;
          getValue: Getter<string>;
        }) => {
          return (
            <Select
              options={formatOwnerOptions(
                row.original.assignableCreditFacilityIds,
                creditFacilitiesMap,
              )}
              value={getValue()}
              onChange={() => {}}
            />
          );
        },
        enableSorting: true,
        enableColumnFilter: true,
      },
    ],
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  return (
    <>
      <Table>
        <Thead>
          {_.map(table.getHeaderGroups(), (headerGroup) => (
            <Tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const canColumnSort = header.column.getCanSort();
                // const canColumnFilter = header.column.getCanFilter();

                return (
                  <Th key={header.id}>
                    <Flex alignItems="center">
                      <Box
                        cursor={canColumnSort ? 'pointer' : 'default'}
                        onClick={header.column.getToggleSortingHandler()}
                        mr="8px"
                      >
                        {{
                          asc: <FaIcon icon="sort-up" />,
                          desc: <FaIcon icon="sort-down" />,
                        }[header.column.getIsSorted() as string] ??
                          (canColumnSort ? <FaIcon icon="sort" /> : null)}
                      </Box>
                      <Box>
                        {flexRender(
                          header.column.columnDef.header,
                          header.getContext(),
                        )}
                      </Box>
                      {/* {canColumnFilter && (
                      <Box ml="8px">
                        <FaIcon icon="chevron-down" size="medium" />
                      </Box>
                    )} */}
                    </Flex>
                  </Th>
                );
              })}
            </Tr>
          ))}
        </Thead>
        <Tbody>
          {table.getRowModel().rows.map((row) => (
            <Tr
              key={row.id}
              // @jendevelops TODO: add in row background color for ineligible receivables that have been assigneds
              // backgroundColor={getRowBGColor(row.original)}
            >
              {row.getVisibleCells().map((cell) => (
                <Td key={cell.id}>
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Td>
              ))}
            </Tr>
          ))}
        </Tbody>
      </Table>
      <TanStackPaginationControl table={table} />
    </>
  );
};
